import "./timelineView.scss";

// import { Stopwatch } from "performance-stopwatch";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import EmptyState from "../../components/emptyState";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { GLOBAL_FILTER_ID, TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import interfaceUserActions from "../../constants/interfaceUserActions";
import { TIMELINE_GROUP_SHOW_ALL } from "../../constants/timelineGroupOptions";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import batchUtils from "../../modules/batchUtils";
import { batchActions } from "../../modules/chainReducer";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { stringFromDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { colorSelectors } from "../../modules/definition/colors";
// import dropSelectors from "../../modules/dropSelectors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { displayCacheSelectors } from "../../modules/display";
import { filterSelectors } from "../../modules/filters";
import { itemOperations, itemSelectors } from "../../modules/items";
import superAndChildrenSelectors from "../../modules/superAndChildrenById/selectors";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { shouldAutoEditItems } from "../../modules/undoRedo";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { trackEvent } from "../../utils/trackAnalytics";
import { appSelectors } from "../app";
import selectors from "./selectors";
import TimelineCanvasView from "./TimelineCanvasView";

// const globalSw = new Stopwatch();
// globalSw.start("TimelineView loaded");

// import debugUtils from "../../utils/debugUtils";

const itemMatchesSuper = (item, checkItemId, itemsById) => {
  const checkItem = itemsById.get(checkItemId);
  // If this item has no parent, return false
  if (!checkItem.super) {
    return false;
  }
  // If this item's parent is the specified item, return true
  if (checkItem.super === item.id) {
    return true;
  }
  // Otherwise, call recursively to see if we have a grandparent match
  return itemMatchesSuper(item, checkItem.super, itemsById);
};

class TimelineView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onEventToggleChildren = this.onEventToggleChildren.bind(this);
    this.onEventDrop = this.onEventDrop.bind(this);
    this.onEventDropToCreateSuper = this.onEventDropToCreateSuper.bind(this);
    this.requestDragPreviewDataForItem = this.requestDragPreviewDataForItem.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.getDropDateResultWithDelta = this.getDropDateResultWithDelta.bind(this);
    this.requestStringForDate = this.requestStringForDate.bind(this);
    this.requestTimespanBetweenDates = this.requestTimespanBetweenDates.bind(this);
    this.getDropDateResult = this.getDropDateResult.bind(this);
    this.onCreateNewEventWithDate = this.onCreateNewEventWithDate.bind(this);
    this.onTimelineClick = this.onTimelineClick.bind(this);
    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);

    // globalSw.start("TimelineView constructed");
  }

  // componentWillReceiveProps(_nextProps) {
  //   debugUtils.logPropChanges("TimelineView", this.props, nextProps, true);
  //   globalSw.lap("TimelineView componentWillReceiveProps");
  // }

  // componentDidUpdate() {
  //   globalSw.lap("TimelineView componentDidUpdate");
  // }

  onEventToggleChildren(id, isExpanded) {
    this.props.onEventToggleChildren(this.props.viewId, id, isExpanded);
  }

  onEventDrop(sourceView, sourceViewType, itemIds, deltaSpan, dropDate, draggingStartArrow, draggingEndArrow, ongoing) {
    // globalSw.lap("TimelineView onEventDrop");

    this.props.onEventDrop(
      this.props.viewId,
      sourceView,
      sourceViewType,
      itemIds,
      deltaSpan,
      dropDate,
      draggingStartArrow,
      draggingEndArrow,
      ongoing
    );
  }

  onEventDropToCreateSuper(sourceView, sourceViewType, itemIds, newSuperId) {
    this.props.onEventDropToCreateSuper(this.props.viewId, sourceView, sourceViewType, itemIds, newSuperId);
  }

  requestDragPreviewDataForItem(itemId, unitScale) {
    return selectors.getTimelineViewDataForItem(
      this.props.localStore,
      itemId,
      unitScale,
      this.props.fontSize,
      this.props.timelineViewSettings.showItemCard,
      this.props.viewId
    );
  }

  requestCanDropItems(dragItem, dropDate, isSettingSuper, newSuperItem) {
    const itemIds = dragItem.multipleItemIds;
    const sourceViewId = dragItem.sourceView;
    const sourceViewType = dragItem.sourceViewType;

    if (isSettingSuper) {
      if (!newSuperItem) {
        return {
          canDrop: true
        };
      }
      // Grouping under existing ancestor is not allowed
      const draggingIntoAncestor = itemIds.find(itemId => itemMatchesSuper(newSuperItem, itemId, this.props.itemsById));
      if (draggingIntoAncestor) {
        return {
          canDrop: false,
          message: interfaceMessages.DRAG_DROP_GROUP_UNDER_ANCESTOR_NOT_ALLOWED
        };
      }
      // Check grouping is allowed based on type can have parent
      const draggingIntoDisallowedSuper = itemIds.find(itemId => {
        const item = this.props.itemsById.get(itemId);
        const type = this.props.typesById.get(item.type);
        return !type.superTypes.includes(newSuperItem.type);
      });
      if (draggingIntoDisallowedSuper) {
        return {
          canDrop: false,
          message: interfaceMessages.DRAG_DROP_GROUP_UNDER_TYPE_NOT_ALLOWED
        };
      }
    }

    if (dragItem.draggingStartArrow && dropDate) {
      if (dragItem.initialValues.dragItemEndDate.timestamp < dropDate.timestamp) {
        return {
          canDrop: false,
          message: interfaceMessages.DRAG_DROP_START_PAST_END_NOT_ALLOWED
        };
      }
    }
    if (dragItem.draggingEndArrow && dropDate) {
      if (dragItem.initialValues.dragItemStartDate.timestamp > dropDate.timestamp) {
        return {
          canDrop: false,
          message: interfaceMessages.DRAG_DROP_END_BEFORE_START_NOT_ALLOWED
        };
      }
    }
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_TIMELINE_NOT_ALLOWED
      };
    }
    if (!itemSelectors.getItemsWithTypeAllowedToDisplayInTimeline(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_DATES_NOT_ALLOWED
      };
    }
    if (!itemSelectors.getItemsAreAllowedToDisplayInTimeline(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_TIMELINE_DISPLAY_NOT_ALLOWED
      };
    }
    if (!itemSelectors.getItemsAreUnlocked(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_LOCKED_DATES
      };
    }
    if (sourceViewType === TIMELINE_VIEW && sourceViewId != this.props.viewId) {
      // Don't allow drop if dragging to another timeline without local filter relationship
      const currentViewLocalFilters = this.props.viewFilter;
      if (!currentViewLocalFilters.relationshipsByType.size && !currentViewLocalFilters.relationshipsByReference.size) {
        if (this.props.sortedItemIds.find(id => itemIds.includes(id))) {
          return {
            canDrop: false,
            message: interfaceMessages.DRAG_DROP_TIMELINE_ALREADY_EXISTS
          };
        } else {
          // Item doesn't exist in the filtered view
          return {
            canDrop: false,
            message: interfaceMessages.DRAG_DROP_NO_EFFECT
          };
        }
      }
    }

    return { canDrop: true };
  }

  getDropDateResultWithDelta(origDate, deltaTs, unitScale, snapToTimespan) {
    return selectors.getDropDateResultWithDelta(this.props.localStore, origDate, deltaTs, unitScale, snapToTimespan);
  }

  requestStringForDate(date) {
    const calendar = calendarSelectors.getCalendar(this.props.localStore);
    return stringFromDate(calendar, date);
  }

  requestTimespanBetweenDates(newDate, oldDate) {
    const calendar = calendarSelectors.getCalendar(this.props.localStore);
    return dateSubtractionFunctions.timespanBetweenDates(calendar, oldDate, newDate);
  }

  getDropDateResult(dropTs, unitScale, snapToTimespan) {
    return selectors.getDropDateResult(this.props.localStore, dropTs, unitScale, snapToTimespan);
  }

  onCreateNewEventWithDate(date, typeId) {
    const useType = typeId ? typeId : this.props.defaultItemType;
    return useType ? this.props.createNewEventWithDate(date, useType, this.props.viewId) : null;
  }

  onTimelineClick(e) {
    const clickWasItem = hasAncestorOfClass(e.target, "timelineItemNode");
    if (!clickWasItem && this.props.selectedItemIds.size > 0) {
      this.props.onClick();
    }
  }

  requestViewBoundsForZoom(zoom) {
    const data = selectors.layoutEvents(
      this.props.timelineData,
      this.props.viewId,
      this.props.ongoingDescendantsSet,
      this.props.topLevelItems,
      this.props.expandedItems,
      zoom,
      this.props.fontSize,
      this.props.calendar,
      this.props.timelineViewSettings.showItemCard,
      this.props.timelineViewSettings.eventLayout
    );
    return data.bounds;
  }

  render() {
    let components = [
      <TimelineCanvasView
        key="timelineScroll"
        timelineData={this.props.timelineData}
        ongoingDescendantsSet={this.props.ongoingDescendantsSet}
        descendantsById={this.props.descendantsById}
        earliestEvent={this.props.earliestEvent}
        latestEvent={this.props.latestEvent}
        shouldAutoScroll={this.props.shouldAutoScroll}
        sortedItemIds={this.props.sortedItemIds}
        selectedItemIds={this.props.selectedItemIds}
        topLevelItems={this.props.topLevelItems}
        expandedItems={this.props.expandedItems}
        timelineViewSettings={this.props.timelineViewSettings}
        viewId={this.props.viewId}
        projectId={this.props.projectId}
        isActiveView={this.props.isActiveView}
        fontSize={this.props.fontSize}
        onSelectItems={this.props.onSelectItems}
        onAddToSelection={this.props.onAddToSelection}
        onRemoveFromSelection={this.props.onRemoveFromSelection}
        onEventToggleChildren={this.onEventToggleChildren}
        onEventDrop={this.onEventDrop}
        onEventDropToCreateSuper={this.onEventDropToCreateSuper}
        onLabelChange={this.props.onLabelChange}
        onCreateNewEventWithDate={this.onCreateNewEventWithDate}
        onCreateNewMarkerWithDate={this.props.createNewMarkerWithDate}
        requestDragPreviewDataForItem={this.requestDragPreviewDataForItem}
        requestCanDropItems={this.requestCanDropItems}
        getDropDateResultWithDelta={this.getDropDateResultWithDelta}
        getDropDateResult={this.getDropDateResult}
        requestStringForDate={this.requestStringForDate}
        requestTimespanBetweenDates={this.requestTimespanBetweenDates}
        requestViewBoundsForZoom={this.requestViewBoundsForZoom}
        onDragHoverValuesChange={this.props.onDragHoverValuesChange}
        validatedDependencies={this.props.validatedDependencies}
        validatedConstraints={this.props.validatedConstraints}
        timelineTypeIds={this.props.timelineTypeIds}
        autoEditBlankNodes={this.props.autoEditBlankNodes}
        itemsById={this.props.itemsById}
        typesById={this.props.typesById}
        propertiesById={this.props.propertiesById}
        colorsById={this.props.colorsById}
        referencesById={this.props.referencesById}
        defaultItemType={this.props.defaultItemType}
        calendar={this.props.calendar}
        dispatchBatchActions={this.props.dispatchBatchActions}
        viewFilter={this.props.viewFilter}
        showItemCard={this.props.showItemCard}
        onShowInspector={this.props.onShowInspector}
      />
    ];

    let emptyState = null;
    // Only show empty state if not using grouping
    if (
      this.props.timelineData.groups.count() === 1 &&
      this.props.timelineData.groups.first().id === TIMELINE_GROUP_SHOW_ALL
    ) {
      if (this.props.timelineData.sortedEventIds.count() === 0) {
        const isFiltered = this.props.isViewFilterActive || this.props.isGlobalFilterActive;
        //#!if readonly === true
        emptyState = (
          <EmptyState type="text" fixed={true}>
            <div key="content">
              <p>No items to display{isFiltered ? " for this filter" : ""}.</p>
            </div>
          </EmptyState>
        );
        //#!else
// emptyState = (
// <EmptyState view={TIMELINE_VIEW} type="view" absolute={false} fixed={true} isFiltered={isFiltered} />
// );
        //#!endif
      }
    }

    const result = (
      <div className="timelineView" onClick={this.onTimelineClick}>
        {components}
        {emptyState}
      </div>
    );

    return result;
  }
}

const mapStateToProps = (state, props) => {
  // globalSw.lap("TimelineView mapStateToProps");

  const timelineData = selectors.getTimelineViewData(state.present, props.viewId);
  const descendantsById = superAndChildrenSelectors.getDescendantsMapFromState(state.present);
  const ongoingDescendantsSet = itemSelectors.getSetOfItemIdsWithOngoingDescendants(state.present);
  const topLevelItems = itemSelectors.getTopLevelItems(state.present);
  const expandedItems = appStateSelectors.getViewExpandedItems(state.present, props.viewId);
  const fontSize = appStateSelectors.getFontSize(state.present);
  const calendar = calendarSelectors.getCalendar(state.present);
  const timelineViewSettings = definitionSelectors.getTimelineViewSettings(state.present);
  const result = {
    timelineData,
    ongoingDescendantsSet,
    descendantsById,
    earliestEvent: itemSelectors.getEarliestTimelineEvent(state.present),
    latestEvent: itemSelectors.getLatestTimelineEvent(state.present),
    calendar,
    topLevelItems,
    expandedItems,
    sortedItemIds: selectors.getTimelineViewSortedItemIds(state.present, props.viewId),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    timelineViewSettings: timelineViewSettings,
    viewFilterLabel: appStateSelectors.getViewFilterLabel(state.present, props.viewId),
    fontSize,
    // Save access to full tree for Drag Preview Data
    localStore: state.present,
    // Function called by the
    defaultItemType: definitionSelectors.getDefaultTimelineType(state.present),
    validatedDependencies: displayCacheSelectors.getValidatedDependencies(state.present),
    validatedConstraints: displayCacheSelectors.getValidatedRelativeConstraints(state.present),
    timelineTypeIds: typeSelectors.getTimelineTypeIds(state.present),
    projectId: appSelectors.getProjectId(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    // Disable auto-edit when applying a REDO action
    autoEditBlankNodes: shouldAutoEditItems(state),
    itemsById: itemSelectors.getItemsById(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    viewFilter: filterSelectors.getFilterWithId(state.present, props.viewId),
    isGlobalFilterActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID),
    isViewFilterActive: filterSelectors.isFilterWithIdActive(state.present, props.viewId),
    filtersById: filterSelectors.getFiltersById(state.present),
    showItemCard: timelineViewSettings.showItemCard
  };

  return result;
};

const readonlyActions = {
  onSelectItems: appStateOperations.selectItems,
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onEventToggleChildren: appStateOperations.toggleExpandSuperItem,
  onClick: appStateOperations.clearSelection,
  onShowInspector: uiStateOperations.showInspectorModal
};

const editingActions = {
  onEventDrop: (
    viewId,
    sourceView,
    sourceViewType,
    itemIds,
    deltaSpan,
    dropDate,
    draggingStartArrow,
    draggingEndArrow,
    ongoing
  ) => {
    let actions = [];
    if (sourceView === viewId) {
      if (!draggingStartArrow && !draggingEndArrow) {
        trackEvent("setItemValueFromTimelineView", "setItemValue", "start");
        actions.push(itemOperations.moveByTimespan(itemIds, deltaSpan.toObject()));
        actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.DRAG_IN_TIMELINE));
      } else if (draggingStartArrow) {
        trackEvent("setItemValueFromTimelineView", "setItemValue", "startArrow");
        if (!ongoing) {
          actions.push(itemOperations.dragStartDateWithFixedEnd(itemIds, dropDate.timestamp, dropDate.precision));
          actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.RESIZE_IN_TIMELINE));
        } else {
          actions.push(itemOperations.setPreferredStartDate(itemIds, dropDate.timestamp, dropDate.precision));
        }
      } else if (draggingEndArrow) {
        trackEvent("setItemValueFromTimelineView", "setItemValue", "endArrow");
        actions.push(itemOperations.dragEndDateWithFixedStart(itemIds, dropDate.timestamp, dropDate.precision));
        actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.RESIZE_IN_TIMELINE));
      }
    } else {
      let innerActions = [];
      if (sourceViewType !== TIMELINE_VIEW) {
        trackEvent("setItemValueFromTimelineViewDropFromOutside", "setItemValue", "start");
        const mainAction = itemOperations.setPreferredStartDate(itemIds, dropDate.timestamp, dropDate.precision);
        innerActions.push(mainAction);
      }
      innerActions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.DROP_ON_TIMELINE));

      // hook them up with match filter, and then batch them with a selection afterwards
      actions.push(batchOperations.matchFiltersAfterDropAction(batchActions(innerActions), itemIds, viewId));
    }

    // And select the items we have dropped
    //#!if iOS !== true
    actions.push(appStateOperations.selectItems(itemIds));
    //#!endif

    return batchActions(actions);
  },
  onEventDropToCreateSuper: (viewId, sourceView, sourceViewType, itemIds, newSuperId) => {
    let actions = [];
    // Set super
    trackEvent("setItemGroupByDragFromTimelineView", "setItemOrderOrGroupByDrag");
    actions.push(itemOperations.setSuper(itemIds, newSuperId));
    actions.push(appStateOperations.selectItems(itemIds));
    let batch = batchActions(actions);

    if (sourceView !== viewId) {
      batch = batchOperations.matchFiltersAfterDropAction(batch, itemIds, viewId);
    }

    return batch;
  },
  onLabelChange: itemOperations.editLabel,
  createNewEventWithDate: (date, itemTypeId, viewId) => {
    trackEvent("createItemFromTimelineView", "createItem", itemTypeId);
    // TODO: get the type id from ...?
    const batchActions = batchUtils.batchWithAppendedActions(
      batchOperations.matchFiltersAfterCreateAction(
        batchOperations.createItemWithStartDate(itemTypeId, date.timestamp, date.precision),
        viewId
      ),
      appStateOperations.userInterfaceActionPerformed(interfaceUserActions.DOUBLE_CLICK_ON_TIMELINE)
    );

    // const itemId = batchActions.actions[0].id;
    return batchActions;
  },
  createNewMarkerWithDate: date => {
    trackEvent("createCalendarMarkerFromTimelineView", "createCalendarMarker");
    return batchOperations.createCalendarMarkerWithStartDate(date.timestamp, date.precision);
  },
  dispatchBatchActions: actions => {
    return batchOperations.reBatchActions(actions);
  },
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

TimelineView.propTypes = {
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  viewFilterLabel: PropTypes.string,
  localStore: PropTypes.object.isRequired,
  timelineData: PropTypes.instanceOf(immutableRecords.TimelineDisplayRecord).isRequired,
  ongoingDescendantsSet: ImmutablePropTypes.setOf(PropTypes.string).isRequired,
  descendantsById: ImmutablePropTypes.map.isRequired,
  earliestEvent: PropTypeShapes.item,
  latestEvent: PropTypeShapes.item,
  shouldAutoScroll: PropTypes.bool,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  topLevelItems: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  expandedItems: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  timelineViewSettings: PropTypes.instanceOf(immutableRecords.TimelineViewSettingsRecord).isRequired,
  timelineTypeIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  defaultItemType: PropTypes.string,
  typesById: ImmutablePropTypes.map,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onEventToggleChildren: PropTypes.func.isRequired,
  onEventDrop: PropTypes.func.isRequired,
  onEventDropToCreateSuper: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  createNewEventWithDate: PropTypes.func.isRequired,
  createNewMarkerWithDate: PropTypes.func.isRequired,
  onShowInspector: PropTypes.func,
  fontSize: PropTypes.number.isRequired,
  onDragHoverValuesChange: PropTypes.func.isRequired,
  validatedDependencies: PropTypeShapes.validatedDependencyMap.isRequired,
  validatedConstraints: ImmutablePropTypes.map.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  viewFilter: PropTypeShapes.filter.isRequired,
  dispatchBatchActions: PropTypes.func,
  filtersById: ImmutablePropTypes.map.isRequired,
  showItemCard: PropTypes.bool.isRequired,
  isViewFilterActive: PropTypes.bool,
  isGlobalFilterActive: PropTypes.bool,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(TimelineView));
